import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo } from 'react';
import { useDrop } from 'react-dnd';

import { GetPitchbookCompanyRawDealsResponse } from '~/services/rtkQuery/company/types';

import { DRAGGABLE_TABLE_ROW, DraggableTableRow } from './components/DraggableTableRow';
import styles from './styles.module.scss';

interface Props {
  name?: string;
  className?: string;
  rawDeals: GetPitchbookCompanyRawDealsResponse['result']['data'];
  onRawDealDrop: (
    rawDeal: GetPitchbookCompanyRawDealsResponse['result']['data'][number],
    dragRoundName?: string,
    dropRoundName?: string,
  ) => void;
}

const Component: React.FC<Props> = ({ name, className, rawDeals, onRawDealDrop }) => {
  const [{ handlerId }, drop] = useDrop<
    { rawDeal: GetPitchbookCompanyRawDealsResponse['result']['data'][number]; roundName?: string },
    void,
    { handlerId: string | symbol | null }
  >({
    accept: DRAGGABLE_TABLE_ROW,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    drop(item) {
      const dragRawDeal = item.rawDeal;
      const dragRoundName = item.roundName;
      const dropRoundName = name;

      // Don't replace items with themselves
      if (dragRoundName === dropRoundName) {
        return;
      }

      onRawDealDrop(dragRawDeal, dragRoundName, dropRoundName);
    },
  });

  return drop(
    <div className={clsx(styles.round, className)} data-handler-id={handlerId}>
      {name && (
        <Typography color="text.primary" fontWeight="700" variant="subtitle1">
          Round {name}
        </Typography>
      )}
      {rawDeals.length > 0 && (
        <table className={styles.round__table}>
          <thead>
            <tr>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Deal No
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Series of Stock
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Deal Date
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Annoucned Date
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Deal Size
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Pre Money Valuation
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Post Valuation
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  % Acquired
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  VC Round
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Deal Type
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Deal Type 2
                </Typography>
              </th>
              <th>
                <Typography color="var(--text-3-color)" fontWeight="300" variant="body1">
                  Deal Synopsis
                </Typography>
              </th>
            </tr>
          </thead>
          <tbody>
            {rawDeals.map(rawDeal => (
              <DraggableTableRow key={rawDeal.dealid} rawDeal={rawDeal} roundName={name} />
            ))}
          </tbody>
        </table>
      )}
    </div>,
  );
};

export const Round = memo(Component);
